import React, { Component } from 'react';
import PropTypes from 'prop-types';
import StyleProcessor from '@ali/vu-style-processor';
import Empty from '@ali/vu-empty';
import RenderError from '@ali/vu-render-error';
import {
  error,
  log,
  warn,
  isObject,
  isFunction,
  getJoinKey,
  getUniqueKey,
  i18nTransform,
  // unwrapCode,
} from '@ali/re-utils';
import Wrapper from './Wrapper';
import StoreEngine from './store';
import ActionEngine from './action';
import DataPool from './datapool';

class Page extends Component {
  static propTypes = {
    context: PropTypes.object,
  };

  static defaultProps = {
    context: {},
  };

  constructor(props) {
    super(props);
    this.ctx = props.context;
    this.initData();
    this.state = {
      isLoading: true,
      isLoadingSchema: !this.hasSchema,
    };
    // if (!this.ctx.fn.renderSection) {
    this.ctx.registerPlugin('renderSection', (id, options = {}, extraProps) => (
      this.renderSection(id, options, extraProps)
    ), false, false);
    // }

    // 获取页面中含有的 section id
    this.ctx.registerPlugin('getSectionIds', () => Object.keys(this.sectionIncludes || {}));

    // renderSchema 插件
    this.ctx.registerPlugin('renderSchema', (schema, _props, isApplyStyle) => this.renderSchemaAsReactElement(schema, _props, isApplyStyle));
  }

  componentDidMount() {
    if (this.hasSchema) {
      this.ctx.emitSchemaReady();
      this.initAction();
      this.initStore();
    }
    this.offPageUpdate = this.ctx.onPageUpdate((schema) => {
      if (!this.hasSchema && this.ctx.isVaildSchema(schema)) {
        this.ctx.saveRawSchema(schema);
        this.ctx.transformSchema(schema);
        this.initData();
        this.ctx.emitSchemaReady();
        this.initAction();
        this.initStore();
        this.setState({
          isLoadingSchema: false,
        });
      }
    });
    this.offPageLeave = this.ctx.onPageLeave(() => {
      StyleProcessor.clearCollection();
    });
    this.ctx.applyMiddleware('AFTERENDER');
  }

  componentWillUnmount() {
    if (this.offPageUpdate) {
      this.offPageUpdate();
    }
    if (this.offPageLeave) {
      this.offPageLeave();
    }
  }

  getComponentView(componentName) {
    return this.ctx.getComponentView(componentName);
  }

  getSectionById(id) {
    const section = this.sectionIncludes[id];
    if (section) {
      return section;
    }
    return null;
  }

  initData() {
    const schema = this.ctx.getSchema(false);
    const pageData = this.ctx.findCurrentPageData(schema.pages);
    this.pageId = pageData.id;
    const currentPath = this.ctx.getCurrentPath();
    if (this.pageId === currentPath || pageData.params && pageData.params.slug === currentPath) {
      this.hasSchema = true;
    }
    this.ctx.setCurrentPageId(this.pageId);
    this.dataPool = pageData.dataPool || pageData.dataSource || {
      offline: [],
      online: [],
      sync: true,
      globalConfig: {},
    };
    this.actionData = (schema.actions || {})[this.pageId];
    this.sectionIncludes = schema.sectionIncludes || {};
    this.processSectionStyle(this.sectionIncludes);
    this.i18nData = schema.i18nData;
    this.pageSchema = pageData.layout;
    const instanceData = (schema.instanceData || {})[this.pageId];
    this.instanceData = this.ctx.transformInstanceData(instanceData);
  }

  initStore() {
    log('=====> initStore(exec datapool)');
    let dataPool;
    if (isObject(this.dataPool)) { // 兼容老数据
      const globalDataPool = this.ctx.getGlobalDataPool();
      dataPool = {
        offline: [...globalDataPool, ...(this.dataPool.offline || [])],
        online: [...globalDataPool, ...(this.dataPool.online || [])],
        globalConfig: this.dataPool.globalConfig || {},
      };
    }
    this.store = this.prepareStore(this.pageId, dataPool || this.dataPool, () => {
      this.dataReady();
    });
  }

  initAction() {
    log('=====> initAction(exec action pane)');
    this.action = this.prepareAction(this.pageId, this.actionData);
  }

  prepareStore(pageId, dataPoolConfig, callback) {
    const store = StoreEngine.create(pageId);
    this.ctx.setStore(pageId, store);
    // 回填context，store parser中使用ctx传递fn及相关action
    store.setContext(this.ctx);
    const dataPool = this.generateDataPool(pageId, dataPoolConfig);
    store.prepare([dataPool]).then(callback, (e) => {
      error(e);
      // https://lark.alipay.com/xfe/docs/front-end
      if (this.ctx.fn.toast && isObject(e) && e.message && e.code >= 0 && e.code <= 399) {
        this.ctx.fn.toast({
          type: 'error',
          title: e.message,
        });
      }
    }).catch((e) => {
      error(e);
    });
    return store;
  }

  prepareAction(pageId, actions) {
    let action = this.ctx.getAction(pageId);
    if (!action) {
      action = ActionEngine.create(pageId);
      action.addAPIs({ router: this.ctx.router });
      this.ctx.registerActionAPIs(action);
      this.ctx.setAction(pageId, action);
      action.setContext(this.ctx);
    }
    const builtinActions = this.ctx.getBuiltinActions();
    action.setBuiltinActions(builtinActions);
    action.setActions(actions);
    return action;
  }

  /**
   * generate a dataPool for page, and save
   *
   * @param {String} pageId
   * @param {Object} config
   */
  generateDataPool(pageId, config) {
    const dataPool = new DataPool(config, this.ctx.getDataPoolType(), this.ctx);
    this.ctx.setDataPool(pageId, dataPool);
    return dataPool;
  }

  /**
   * recursively process component schema
   * 1. form submit / default button behavior changed by form
   * 2. 118n transform
   * 3. merge global validation
   * 4. merge global events
   * 5. collect child fieldId
   * 6. collect style
   * @param {Object} componentSchema schema need process
   * @param {Object} extrasProps extra props need to be pend in schema
   * @param {Bool} i18n need i18n transform
   * @param {Object} fieldIds field ids will be collected in it
   */
  transformComponent(componentSchema, extrasProps = {}, i18n = false, fieldIds) {
    if (!componentSchema) { // 空页面时, schema.layout 为 undefined
      return null;
    }
    const {
      formData = {},
      flowData = {},
      behaviorData = {},
      propsData = {},
    } = this.instanceData;
    let { componentName, props = {}, children, ...extras } = componentSchema; // eslint-disable-line

    if (i18n) {
      props = i18nTransform(props, this.ctx.getLocale(), this.i18nData);
    }

    // hotprops 中解析，在次忽略
    // props = unwrapCode(props);

    const { fieldId } = props;
    const globalProps = this.ctx.getGlobalProps(componentName);

    let fieldProps = null;
    if (fieldId) {
      const dataType = formData[fieldId] ? 'SUBMIT' : 'DEFAULT';
      let behavior = {
        fieldBehavior: 'NORMAL',
        ...props.defaultBehavior,
      };

      if (flowData.viewMode && behavior.fieldBehavior === 'NORMAL') {
        behavior.fieldBehavior = 'READONLY';
      }
      behavior = {
        ...behavior,
        ...behaviorData[fieldId],
        viewMode: flowData.viewMode,
      };
      let validation;
      if (Array.isArray(props.validation) || !props.validation) {
        validation = (globalProps.validation || []).concat(props.validation || []);
      } else {
        ({ validation } = props);
        validation.__mergeFromGlobal = globalProps.validation;
      }
      fieldProps = {
        ...(propsData[fieldId] ? propsData[fieldId].fieldProps : {}),
        fieldData: props.defaultValue || {},
        ...formData[fieldId],
        behavior,
      };
      if (validation.length || props.validation) {
        fieldProps.validation = validation;
      }
      // 区分数据是默认值还是表单提交的数据
      fieldProps.fieldData.dataType = dataType;
      // 不同的平台事件交互不同, 通过 globalEvent 来覆盖原有的事件
      const globalEvent = this.ctx.getGlobalEvents()[fieldId];
      if (globalEvent) {
        fieldProps.events = globalEvent;
      }
      if (fieldIds) {
        fieldIds[fieldId] = true;
      }
    }

    props = {
      ...globalProps,
      ...props,
      ...fieldProps,
      ...extrasProps,
    };

    StyleProcessor.collectStyle(extras.id || extras.componentId, componentName, props);

    let nextFieldIds = fieldIds;
    if (props.createForm || props.createScope) {
      extras.childFieldIds = {};
      nextFieldIds = extras.childFieldIds;
    }

    children = Array.isArray(children)
      ? children.map(child => this.transformComponent(child, extrasProps, i18n, nextFieldIds))
      : null;

    return {
      ...extras,
      componentName,
      props,
      children,
    };
  }

  dataReady() {
    this.pageSchema.childFieldIds = {};
    const schema = this.transformComponent(this.pageSchema, {}, false, this.pageSchema.childFieldIds);
    const { isLoading } = this.state;

    // exec only once
    if (isLoading) {
      this.ctx.emitDataReady();
    }

    // 插入样式
    StyleProcessor.applyStyle();

    this.setState({
      schema,
      isLoading: false,
    }, () => {
      // exec only once
      if (isLoading) {
        this.ctx.emitPageReady();
        log('-----> rendered plugin:', this.ctx.getRenderedPlugin());
        log('-----> rendered component:', this.ctx.getRenderedComponent());
      }
    });
  }

  wrapSection(section, options, id) {
    const extraProps = {
      __iteratorId: options.id,
      __passThrough: {
        [options.key || 'scope']: options,
      },
    };
    let s = section;
    if (Array.isArray(section) && section.length === 1) {
      ([s] = section);
    }
    if (isObject(s)) {
      if (s.componentName === 'Section') {
        // 这种情况 section 自身节点拿不到 scope
        // section.id = id;
        s.props = { ...s.props, ...extraProps };
        return s;
      }
      s = [s];
    }
    // 为了透传数据,必须用包裹一层
    return {
      componentName: 'Section',
      id,
      children: s,
      props: extraProps,
    };
  }

  collectSectionStyle(componentSchema) {
    if (!componentSchema) {
      return;
    }
    if (Array.isArray(componentSchema)) {
      componentSchema.forEach(schema => this.collectSectionStyle(schema));
      return;
    }
    let { componentName, props = {}, children, ...extras } = componentSchema; // eslint-disable-line
    StyleProcessor.collectStyle(extras.id || extras.componentId, componentName, props);
    if (Array.isArray(children)) {
      children.forEach(child => this.collectSectionStyle(child));
    }
  }

  processSectionStyle(sections) {
    Object.keys(sections).forEach((key) => {
      this.collectSectionStyle(sections[key]);
    });
  }

  /**
   * render a symbol | reference section
   * @param {string} id symbolId or referenceTemplate id
   * @param {object} options { key: the __passThrough scope name, ...passedProps }
   * @param {*} extraProps extraProps adding on the Section or Symbol props
   */
  renderSection(id, options, extraProps = {}) {
    let section;
    let sectionId;
    if (isObject(id)) {
      section = id;
      sectionId = getJoinKey('node', getUniqueKey());
    } else {
      section = this.getSectionById(id);
      sectionId = id;
    }
    if (section) {
      const schema = this.transformComponent(
        this.wrapSection(section, options, sectionId),
        {},
        true
      );
      /**
       * Convert callback function to string while using
       * ctx.fn.renderSection(id, passProps, {
       *   events: {
       *     onChange(ctx, val) { ... }
       *   }
       * })
       */
      // 后面改变props较多，先复制一份
      schema.props = { ...schema.props };
      if (extraProps.events) {
        if (isObject(extraProps.events)) {
          Object.keys(extraProps.events).forEach((key) => {
            if (isFunction(extraProps.events[key])) {
              extraProps.events[key] = [{
                func: extraProps.events[key].toString(),
              }];
            }
          });
        }
        schema.props.events = extraProps.events;
      }
      // fieldId用于绑定section到对应的key上。
      // 元件两次渲染时，如果没有fieldId, 一个元件的状态会存储在两个地方
      // 一个在substate里面，一个在section所在的状态树，放置fieldId可以保证每个section
      // 只有一个状态树。
      if (extraProps.fieldId) {
        schema.props.fieldId = extraProps.fieldId;
      }
      // options保证section中的配置选项同步
      if (extraProps.options) {
        schema.props.options = extraProps.options;
      }
      // 指定isSymbol = true。标注自身时一个元件
      schema.props.isSymbol = true;
      // 元件名称指定
      if (extraProps.symbolName != null) {
        schema.props.symbolName = extraProps.symbolName;
      }
      if (extraProps.fieldId) {
        schema.props.fieldId = extraProps.fieldId;
      }
      schema.props.isSymbol = extraProps.isSymbol;
      schema.props.symbolName = extraProps.symbolName;
      extraProps.__sectionRendered = true;
      return this.renderSchema(schema, extraProps);
    }
    log(`SectionId ${id} is not found.`);
    return (
      <RenderError message="引用区块不存在" />
    );
  }

  renderSchema(componentSchema, extraPropsMap = {}) {
    const extraProps = { ...extraPropsMap };
    let key = componentSchema.id;
    if (componentSchema.props.__iteratorId) {
      const iteratorId = componentSchema.props.__iteratorId;
      key = getJoinKey(iteratorId, key);
      extraProps.__iteratorId = iteratorId;
    }

    // 处理区块渲染
    if (componentSchema.props.__passThrough) {
      extraProps.__passThrough = componentSchema.props.__passThrough;
    }

    const wrapperProps = {
      context: this.ctx,
      pageId: this.pageId, // TODO： 如需将context api注入在整个Page周期中
      store: this.store,
      action: this.action,
      ...extraProps,
      componentSchema,
    };
    this.ctx.setRenderedComponent([]);
    log('=====> start render components.');
    return (
      <Wrapper {...wrapperProps} key={key} />
    );
  }

  /**
   * 渲染一段 schema 为 React Element
   * @param componentSchema
   * @param extraProps
   * @param isApplyStyle
   * @return {*}
   */
  renderSchemaAsReactElement(componentSchema, extraProps = {}, isApplyStyle = false) {
    if (componentSchema) {
      const schema = this.transformComponent(componentSchema, {}, true);
      if (extraProps.events) {
        if (isObject(extraProps.events)) {
          Object.keys(extraProps.events).forEach((key) => {
            if (isFunction(extraProps.events[key])) {
              extraProps.events[key] = [{
                func: extraProps.events[key].toString(),
              }];
            }
          });
        }
        schema.props = { ...schema.props, events: extraProps.events };
      }
      const {
        id, componentId, componentName, props,
      } = schema;
      // 是否应用组件中的 style
      if (isApplyStyle) {
        StyleProcessor.collectStyle(id || componentId, componentName, props);
        StyleProcessor.applyStyle();
        // StyleProcessor.clearCollection();
      }
      // todo 清除样式
      const { $state, $props } = extraProps;
      return this.renderSchema(schema, {
        __ownerScope: $state ? $state.getClosestScope() : null,
        // 指定parentXprops，用于将新的section放置在状态树上，否则就都直接在StoreEngine下了
        parentXprops: $props || null,
      });
    }
    return (
      <RenderError message="区块不存在" />
    );
  }

  render() {
    const { isLoading, schema, isLoadingSchema } = this.state;
    // pageSchema should be Object
    if (!this.pageSchema && !isLoadingSchema) {
      warn('The schema of current page is empty.');
      log('=====page render=====.Current schema: ', this.ctx.getSchema());
      return (
        <Empty locale={this.ctx.getLocale()} />
      );
    }
    // display loading before data ready
    if (isLoading || !schema) {
      if (navigator.serviceWorker && navigator.serviceWorker.controller) { // SW
        const pageContainer = document.getElementById('render-engine-page-container');
        if (pageContainer) {
          return <div dangerouslySetInnerHTML={{ __html: pageContainer.innerHTML }} />;
        }
      }
      return this.ctx.loading;
    }
    return (
      <div id="render-engine-page-container">
        {this.renderSchema(schema)}
      </div>
    );
  }
}

export default Page;
